import { useState } from "react";
import { Reveal } from "@/components/site/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { WhatsappLogo, Phone, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { api, trackEvent } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { toast } from "sonner";

const initial = { name: "", phone: "", email: "", preferred_date: "", preferred_time: "", reason: "", message: "" };

export function Appointment() {
  const { content } = useContent();
  const c = content.contact;
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  const upd = (k) => (e) => {
    if (!started) { trackEvent("appointment_form_started"); setStarted(true); }
    setForm({ ...form, [k]: e.target.value });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please provide your name and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", form);
      trackEvent("appointment_form_submitted");
      setDone(true);
      toast.success("Your appointment request has been received.");
      setForm(initial);
    } catch (_) {
      toast.error("Could not submit right now. Please contact the clinic directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const wa = c.whatsapp.replace(/[^0-9]/g, "");

  return (
    <section id="appointment" className="py-24 md:py-32 bg-[#F4F7FA] scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="label">Appointment</span>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-tight mt-4 text-[#0A192F]">
                Schedule a Consultation
              </h2>
              <p className="mt-5 text-lg text-[#475569] leading-relaxed">
                Submit an enquiry and the clinic will respond with appointment guidance. Requests are not auto-confirmed.
              </p>
            </Reveal>

            <Reveal delay={0.15} className="mt-8 space-y-3">
              <a
                href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
                onClick={() => trackEvent("whatsapp_clicked")}
                data-testid="whatsapp-btn"
                className="flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:-translate-y-0.5 transition-transform duration-300"
              >
                <WhatsappLogo size={26} weight="duotone" className="text-[#0A192F]" />
                <span><span className="block text-[#0A192F] font-medium">Prefer WhatsApp?</span><span className="text-sm text-[#475569]">{c.whatsapp}</span></span>
              </a>
              <a
                href={`tel:${c.phone.replace(/\s/g, "")}`}
                onClick={() => trackEvent("phone_clicked")}
                data-testid="appointment-phone-btn"
                className="flex items-center gap-3 bg-white border border-[#E2E8F0] rounded-2xl p-5 hover:-translate-y-0.5 transition-transform duration-300"
              >
                <Phone size={26} weight="duotone" className="text-[#0A192F]" />
                <span><span className="block text-[#0A192F] font-medium">Contact the clinic directly</span><span className="text-sm text-[#475569]">{c.phone}</span></span>
              </a>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div className="bg-white border border-[#E2E8F0] rounded-3xl p-10 h-full flex flex-col items-start justify-center" data-testid="appointment-success">
                  <CheckCircle size={48} weight="duotone" className="text-[#0A192F]" />
                  <h3 className="font-display text-3xl mt-4 text-[#0A192F]">Request Received</h3>
                  <p className="mt-3 text-[#475569] max-w-md leading-relaxed">
                    Thank you. Your consultation request has been recorded. The clinic will review it and respond with appointment guidance.
                  </p>
                  <button onClick={() => setDone(false)} className="mt-6 text-sm text-[#0A192F] underline underline-offset-4" data-testid="appointment-new-request">
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white border border-[#E2E8F0] rounded-3xl p-6 md:p-9" data-testid="appointment-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Name *"><Input data-testid="apt-name" value={form.name} onChange={upd("name")} placeholder="Your full name" className="h-12" /></Field>
                    <Field label="Phone Number *"><Input data-testid="apt-phone" value={form.phone} onChange={upd("phone")} placeholder="Contact number" className="h-12" /></Field>
                    <Field label="Email"><Input data-testid="apt-email" type="email" value={form.email} onChange={upd("email")} placeholder="you@example.com" className="h-12" /></Field>
                    <Field label="Preferred Date"><Input data-testid="apt-date" value={form.preferred_date} onChange={upd("preferred_date")} placeholder="e.g. 12 July 2026" className="h-12" /></Field>
                    <Field label="Preferred Time"><Input data-testid="apt-time" value={form.preferred_time} onChange={upd("preferred_time")} placeholder="e.g. Morning / 11 AM" className="h-12" /></Field>
                    <Field label="Reason for Consultation"><Input data-testid="apt-reason" value={form.reason} onChange={upd("reason")} placeholder="Brief reason" className="h-12" /></Field>
                  </div>
                  <div className="mt-5">
                    <Field label="Message"><Textarea data-testid="apt-message" value={form.message} onChange={upd("message")} placeholder="Anything else you'd like to share" rows={4} /></Field>
                  </div>
                  <button
                    type="submit" disabled={submitting}
                    data-testid="apt-submit"
                    className="btn-accent mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold transition-[transform,background-color] duration-300 disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Request Appointment"} <PaperPlaneTilt size={18} weight="bold" />
                  </button>
                  <p className="mt-4 text-xs text-[#64748B] italic">
                    Note: Submitting this form sends an enquiry to the clinic. It does not confirm a booking. This form is ready to connect to email, CRM, WhatsApp or an appointment system.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="label text-[0.55rem]">{label}</Label>
      {children}
    </div>
  );
}
